"use client";

import React from "react";
import { createPortal } from "react-dom";

// Phiếu in "Phiếu giao nhận" — A5 ngang, đen trắng. Portal vào <body>, ẩn trên màn hình (`hidden print:block`).
// Chỉ còn đúng 1 đoạn CSS global bắt buộc: khổ giấy + ẩn phần còn lại của trang khi in (tailwind không chọn được phần tử anh em).
const PRINT_ONLY_CSS = `@media print {
  @page { size: A5 landscape; margin: 8mm; }
  body > *:not(.reconcile-print-root) { display: none !important; }
}`;

export interface ClassifyGroup {
  mode: string;
  items: { category: string; quantity: number; category_id?: number | null }[];
  total?: number;
}

// Cột nợ: nợ, điều chỉnh(đc), thất thoát (tt), không chuẩn (kc), giao dư (gd), 
const DEBT_LABEL: Record<number, string> = { 1: "nợ", 3: "đc", 4: "tt", 5: "kc", 6: "gd" };
const PAID_TYPE = 2; // trả nợ

const pad = (n: number) => String(n).padStart(2, "0");
const signed = (n: number) => (n > 0 ? `+${n}` : `−${Math.abs(n)}`);
const fmtDate = (v: any) => {
  const d = v ? new Date(v) : null;
  return d && !isNaN(d.getTime()) ? `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}` : "—";
};
const normName = (v: any) => String(v || "").replace(/\(.*?\)/g, "").trim().toLowerCase();

// Class dùng chung. `!p-…`: tailwind.css có luật global `table tbody tr td { py-3 px-4 }` làm hàng cao gấp đôi.
const TH = "border border-black !px-1 !py-0.5 text-center font-bold";
const TD = "border border-black !px-1 !py-0.5";

interface Props {
  order: any;
  classifyGroups: ClassifyGroup[];
}

const ReconcilePrintSheet: React.FC<Props> = ({ order, classifyGroups }) => {
  if (typeof document === "undefined" || classifyGroups.length === 0) return null;

  console.log('order', order)
  console.log('classifyGroups', classifyGroups)

  const groupOf = (mode: string) => classifyGroups.find((g) => g.mode === mode);
  const qtyOf = (mode: string, cat: string) => {
    const it = groupOf(mode)?.items.find((i) => i.category === cat);
    return it ? Number(it.quantity) : "";
  };

  // Khách có kiểm đếm: `order.requires_counting` (API order-laundry/:id/detail).
  const showCounting = Number(order?.requires_counting) === 1;
  const factoryMode = groupOf("transfer_check") ? "transfer_check" : groupOf("inspect") ? "inspect" : "";

  const cols = [
    showCounting && { head: "KĐ nhận", mode: "counting" },
    factoryMode && { head: "Xưởng giặt", mode: factoryMode },
    { head: "Hoàn thành giặt", mode: "production_done" },
    showCounting && { head: "KĐ giao", mode: "delivery_check" },
  ].filter(Boolean) as { head: string; mode: string }[];

  // Hàng = hợp các loại đồ của mọi chặng.
  const categories = Array.from(new Set(classifyGroups.flatMap((g) => g.items.map((i) => i.category).filter(Boolean))));

  // Sổ nợ của đơn (chỉ đơn kiểm đếm), khớp dòng theo category_id rồi theo tên.
  const debts: any[] = showCounting && Array.isArray(order?.item_debt_by_category) ? order.item_debt_by_category : [];
  const idOf = (cat: string) => classifyGroups.flatMap((g) => g.items).find((i) => i.category === cat)?.category_id;
  const debtOf = (cat: string) =>
    debts.filter((d) => (d.category_id && idOf(cat) ? Number(d.category_id) === Number(idOf(cat)) : normName(d.category) === normName(cat)));
  const owedText = (cat: string) =>
    debtOf(cat).filter((d) => Number(d.entry_type) !== PAID_TYPE).map((d) => `${signed(-Number(d.quantity))} (${DEBT_LABEL[Number(d.entry_type)] ?? ""})`);
  const paidText = (cat: string) =>
    debtOf(cat).filter((d) => Number(d.entry_type) === PAID_TYPE).map((d) => signed(-Number(d.quantity)));
  const showDebt = 1 ||  debts.length > 0;

  // ĐVT: dòng hàng của đơn có tên chứa tên loại đồ; không thấy → "cái".
  const unitOf = (cat: string) => {
    const key = normName(cat);
    const hit = (order?.items || []).find((it: any) => key && normName(it.item_name).includes(key));
    return hit?.unit_name || "cái";
  };

  const kg = Number(order?.final_weight) > 0 ? `${Math.round(Number(order.final_weight) * 100) / 100} kg` : ".......... kg";
  const now = new Date();

  return createPortal(
    <div className="reconcile-print-root hidden print:block font-sans text-black text-[8.5pt] leading-tight">
      <style>{PRINT_ONLY_CSS}</style>

      <div className="flex justify-between items-start mb-1">
        <div className="text-[15pt] font-bold">Giặt24H</div>
        <div className="text-right">
          <div className="text-[12pt] font-bold uppercase">Phiếu giao nhận</div>
          <div>Số: {order?.code ?? `OD-${order?.id}`}</div>
        </div>
      </div>

      <div>
        <div><b>Khách hàng:</b> {order?.customer?.name ?? "—"}</div>
        <div><b>Địa chỉ khách hàng:</b> {order?.address ?? "—"}</div>
        <div><b>Ngày:</b> {fmtDate(order?.created_at)}</div>
      </div>

      <table className="w-full border-collapse table-fixed mt-1 text-[8pt]">
        <thead className="table-header-group">
          <tr>
            <th className={`${TH} w-[5%]`}>STT</th>
            <th className={`${TH} w-[25%]`}>Mặt hàng</th>
            <th className={`${TH} w-[5%]`}>ĐVT</th>
            {cols.map((c) => <th key={c.mode} className={TH}>{c.head}</th>)}
            {showDebt && <th className={`${TH} w-[8%]`}>Nợ</th>}
            {showDebt && <th className={`${TH} w-[8%]`}>Trả</th>}
            <th className={`${TH} w-[8%]`}>Ghi chú</th>
          </tr>
        </thead>
        <tbody>
          {categories.map((cat, i) => (
            <tr key={cat} className="break-inside-avoid">
              <td className={`${TD} text-center`}>{pad(i + 1)}</td>
              <td className={`${TD} break-words`}>{cat}</td>
              <td className={`${TD} text-center`}>{unitOf(cat)}</td>
              {cols.map((c) => <td key={c.mode} className={`${TD} text-center font-bold`}>{qtyOf(c.mode, cat)}</td>)}
              {showDebt && <td className={`${TD} text-center`}>{owedText(cat).map((t) => <div key={t}>{t}</div>)}</td>}
              {showDebt && <td className={`${TD} text-center`}>{paidText(cat).map((t, k) => <div key={k}>{t}</div>)}</td>}
              <td className={TD} />
            </tr>
          ))}
          <tr>
            <td colSpan={3 + cols.length + (showDebt ? 2 : 0)} className={`${TD} text-right font-bold`}>Tổng cộng: {kg}</td>
            <td className={TD} />
          </tr>
        </tbody>
      </table>

      <div className="text-right mt-1">Ngày {pad(now.getDate())} Tháng {pad(now.getMonth() + 1)} Năm {now.getFullYear()}</div>
      <div className="grid grid-cols-2 mt-1 text-center break-inside-avoid">
        {["Người Nhận", "Người Giao"].map((role) => (
          <div key={role}>
            <div className="font-bold">{role}</div>
            <div className="text-[7pt] italic">(Ký, ghi rõ họ tên)</div>
            <div className="h-12" />
          </div>
        ))}
      </div>
    </div>,
    document.body,
  );
};

export default ReconcilePrintSheet;
